import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AppShell } from '../components/AppShell';
import { QuickAddFAB } from '../components/QuickAddFAB';
import { RéserveFormSheet } from '../components/reserves/ReserveFormSheet';
import { RéserveCard } from '../components/reserves/ReserveCard';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { DeleteConfirmDialog } from '../components/DeleteConfirmDialog';
import {
  Plus, ClipboardText, MagnifyingGlass, FunnelSimple,
  ArrowLeft
} from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export function ReservesPage() {
  const [réserves, setRéserves] = useState([]);
  const [chantiers, setChantiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterChantier, setFilterChantier] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingRéserve, setEditingRéserve] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({
    chantier_id: '', title: '', description: '',
    location: '', priority: 'medium', lot: '', responsible: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [transcription, setTranscription] = useState('');
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const [réservesRes, chantiersRes] = await Promise.all([
        axios.get(`${API}/reserves`, { withCredentials: true }),
        axios.get(`${API}/chantiers`, { withCredentials: true })
      ]);
      setRéserves(réservesRes.data || []);
      setChantiers((chantiersRes.data || []).filter(c => c && c.id && c.name));
    } catch (error) {
      setRéserves([]);
      setChantiers([]);
    } finally {
      setLoading(false);
    }
  }, [setRéserves, setChantiers, setLoading]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreateSheet = () => {
    setEditingRéserve(null);
    setFormData({ chantier_id: '', title: '', description: '', location: '', priority: 'medium', lot: '', responsible: '' });
    setSelectedPhotos([]);
    setTranscription('');
    setIsSheetOpen(true);
  };

  const openEditSheet = (réserve) => {
    setEditingRéserve(réserve);
    setFormData({
      chantier_id: réserve.chantier_id || '', title: réserve.title || '',
      description: réserve.description || '', location: réserve.location || '',
      priority: réserve.priority || 'medium', lot: réserve.lot || '',
      responsible: réserve.responsible || ''
    });
    setSelectedPhotos([]);
    setTranscription('');
    setIsSheetOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingRéserve) {
        await axios.put(`${API}/réserves/${editingRéserve.id}`, formData, { withCredentials: true });
      } else {
        const response = await axios.post(`${API}/reserves`, {
          ...formData,
          description: transcription ? `${formData.description}\n\n[Note vocale]: ${transcription}` : formData.description
        }, { withCredentials: true });
        if (selectedPhotos.length > 0) {
          for (const photo of selectedPhotos) {
            const photoFormData = new FormData();
            photoFormData.append('file', photo);
            await axios.post(
              `${API}/upload?réserve_id=${response.data.id}&chantier_id=${formData.chantier_id}`,
              photoFormData,
              { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
            );
          }
        }
      }
      setIsSheetOpen(false);
      setEditingRéserve(null);
      fetchData();
    } catch (_err) { toast.error('Erreur sauvegarde réserve'); }
    finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (réserveId) => {
    try {
      await axios.delete(`${API}/reserves/${réserveId}`, { withCredentials: true });
      setDeleteConfirm(null);
      fetchData();
    } catch (_err) { toast.error('Erreur suppression réserve'); }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      mediaRecorder.ondataavailable = (event) => { audioChunksRef.current.push(event.data); };
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        const formDataUpload = new FormData();
        formDataUpload.append('file', audioBlob, 'voice_note.webm');
        try {
          const response = await axios.post(`${API}/voice-notes/transcribe`, formDataUpload, {
            withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
          });
          setTranscription(response.data.transcription || '');
        } catch (_err) { toast.error('Erreur transcription audio'); }
      };
      mediaRecorder.start();
      setIsRecording(true);
    } catch (_err) { toast.error('Erreur acces microphone'); }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const updateRéserveStatus = async (réserveId, newStatus) => {
    try {
      await axios.put(`${API}/reserves/${réserveId}`, { status: newStatus }, { withCredentials: true });
      fetchData();
    } catch (_err) { toast.error('Erreur mise a jour réserve'); }
  };


  const filteredRéserves = (réserves || []).filter(r => {
    if (!r) return false;
    const matchesSearch = (r.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (r.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (r.location || '').toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch
      && (filterChantier === 'all' || r.chantier_id === filterChantier)
      && (filterStatus === 'all' || r.status === filterStatus);
  });

  const getChantierName = (chantierId) => {
    return (chantiers || []).find(c => c && c.id === chantierId)?.name || 'N/A';
  };

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-10 h-10 animate-spin text-[#0055FF]" />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="space-y-6" data-testid="réserves-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-10 w-10 md:hidden"
              onClick={() => {
                if (isSheetOpen && !window.confirm('Vous avez un formulaire ouvert. Quitter sans enregistrer ?')) return;
                navigate('/dashboard');
              }} data-testid="back-btn">
              <ArrowLeft size={22} />
            </Button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-[#111827]">Réserves</h1>
              <p className="text-[#4B5563] mt-1">
                {(réserves || []).filter(r => r && r.status === 'open').length} réserve(s) ouverte(s) sur {réserves.length}
              </p>
            </div>
          </div>
          <Button onClick={openCreateSheet} className="bg-[#0055FF] hover:bg-blue-700" data-testid="new-réserve-btn">
            <Plus size={20} className="mr-2" /> Nouvelle réserve
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B5563]" />
            <Input placeholder="Rechercher..." value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-14" data-testid="réserve-search-input" />
          </div>
          <Select value={filterChantier} onValueChange={setFilterChantier}>
            <SelectTrigger className="w-full md:w-48 h-14" data-testid="filter-chantier-select">
              <FunnelSimple size={18} className="mr-2" />
              <SelectValue placeholder="Chantier" />
            </SelectTrigger>
            <SelectContent position="popper" className="z-[100]">
              <SelectItem value="all" className="py-3">Tous les chantiers</SelectItem>
              {(chantiers || []).filter(c => c && c.id && c.name).map(c => (
                <SelectItem key={c.id} value={String(c.id)} className="py-3">{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full md:w-36 h-14" data-testid="filter-status-select">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent position="popper" className="z-[100]">
              <SelectItem value="all" className="py-3">Tous</SelectItem>
              <SelectItem value="open" className="py-3">En cours</SelectItem>
              <SelectItem value="closed" className="py-3">Levee</SelectItem>
              <SelectItem value="pending" className="py-3">En attente</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Réserves List */}
        <div className="grid gap-4">
          {filteredRéserves.length > 0 ? filteredRéserves.map((réserve) => (
            <RéserveCard
              key={réserve.id}
              réserve={réserve}
              chantierName={getChantierName(réserve.chantier_id)}
              onEdit={openEditSheet}
              onDelete={setDeleteConfirm}
              onUpdateStatus={updateRéserveStatus}
            />
          )) : (
            <Card className="chantix-card">
              <CardContent className="py-12 text-center">
                <ClipboardText size={48} className="mx-auto mb-4 text-[#4B5563] opacity-50" />
                <h3 className="font-semibold text-[#111827] mb-2">Aucune réserve</h3>
                <p className="text-[#4B5563] mb-4">
                  {searchTerm || filterChantier !== 'all' || filterStatus !== 'all'
                    ? 'Modifiéz vos filtres pour voir les réserves'
                    : 'Commencez par créer une réserve'}
                </p>
                {!searchTerm && filterChantier === 'all' && filterStatus === 'all' && (
                  <Button className="bg-[#0055FF] hover:bg-blue-700" onClick={openCreateSheet}>
                    <Plus size={20} className="mr-2" /> Nouvelle réserve
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <RéserveFormSheet
        open={isSheetOpen} onOpenChange={setIsSheetOpen}
        chantiers={chantiers} editingRéserve={editingRéserve}
        formData={formData} setFormData={setFormData}
        submitting={submitting} selectedPhotos={selectedPhotos}
        setSelectedPhotos={setSelectedPhotos}
        isRecording={isRecording} transcription={transcription}
        onSubmit={handleSubmit}
        onStartRecording={startRecording}
        onStopRecording={stopRecording}
      />

      <DeleteConfirmDialog
        item={deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={handleDelete}
        titlePrefix="Supprimér la réserve"
        entityLabel={deleteConfirm?.title}
      />

      <QuickAddFAB chantiers={chantiers} />
    </AppShell>
  );
}
